import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { useIntl } from 'react-intl';

import {
  resolveActingTenantId,
  getTenantOverride,
  setTenantOverride,
} from '../../api/tenantOverride';
import { useAuthContext } from '../../context/AuthProvider';
import useTenants from '../../hooks/tenants/useTenants';
import messages from './messages';

const MY_TENANT = 'mine';

const TENANTS_FILTERS = {
  limit: '100',
  // BE QueryAllDto.offset is a 1-based page number (min 1), not a row offset
  offset: '1',
  orderBy: 'name',
  orderDirection: 'asc',
} as const;

/**
 * Super-admin tenant switcher (premium-content workstream), rendered at the top
 * of the sidebar's platform block: picks the tenant
 * the WHOLE app operates on (X-Tenant-Id override + reload). Rendered only for
 * platform users with `tenants_read` (in practice: platform_super_admin — the
 * backend rejects cross-tenant requests for any other role).
 */
const TenantSwitcherSelect = () => {
  const { formatMessage } = useIntl();
  const { data: tenants } = useTenants(TENANTS_FILTERS);

  // Through `resolveActingTenantId`, not a hand-written `??`: an empty string
  // in storage is NOT an override — axios and `/admin/rebrand` read it as
  // absent through this same rule — and `??` kept it, leaving the Select on a
  // value matching no option while every request acted on the domain tenant.
  const value = resolveActingTenantId(getTenantOverride()) ?? MY_TENANT;

  const options = tenants?.data ?? [];

  // An override the option list does not contain — the list is still in
  // flight, the tenant sits past `limit: 100`, or it has since been deleted.
  //
  // The Select cannot simply be pointed at `MY_TENANT` then: `X-Tenant-Id` is
  // rebuilt from STORAGE on every request, so the override is already acting
  // whatever this control displays. Left unmatched it rendered an EMPTY
  // selection — the same lie the `''` reading above fixes, told from the other
  // side: a header claiming no override while every request carries one. So
  // the id gets an option of its own and the switcher keeps naming the tenant
  // it is actually on, by id until the list can supply the name.
  const unlistedOverride =
    value !== MY_TENANT && !options.some((tenant) => tenant.id === value)
      ? value
      : null;

  const handleChange = (nextValue: string) => {
    setTenantOverride(nextValue === MY_TENANT ? null : nextValue);
    // full reload: every query cache entry belongs to the previous tenant
    window.location.reload();
  };

  return (
    <TextField
      onChange={(event) => handleChange(event.target.value)}
      label={formatMessage(messages.label)}
      inputProps={{ 'data-testid': 'tenant-switcher' }}
      fullWidth
      value={value}
      size="small"
      select
    >
      <MenuItem data-testid="tenant-switcher-option" value={MY_TENANT}>
        {formatMessage(messages.myTenant)}
      </MenuItem>
      {unlistedOverride && (
        <MenuItem
          data-testid="tenant-switcher-option"
          value={unlistedOverride}
          key={unlistedOverride}
        >
          {unlistedOverride}
        </MenuItem>
      )}
      {options.map((tenant) => (
        <MenuItem
          data-testid="tenant-switcher-option"
          value={tenant.id}
          key={tenant.id}
        >
          {tenant.name}
        </MenuItem>
      ))}
    </TextField>
  );
};

export const TenantSwitcher = () => {
  const { currentUser } = useAuthContext();
  // F1b (platform-user-class): after the P5 bypass every platform user holds
  // `tenants_read`, so the permission no longer separates the two platform
  // roles — the switcher is super-admin-only by the explicit flag.
  // platform_admin is tenant-locked and has nothing to switch.
  if (!currentUser?.isSuperAdmin) {
    return null;
  }
  return <TenantSwitcherSelect />;
};
